import React, { useEffect, useState } from "react";
import { Col, Collapse, Divider, Form, Modal, Row, Tooltip } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { useHistory, useLocation, useParams } from "react-router";
import moment from "moment";
import { round } from "lodash";
import { useForm } from "antd/lib/form/Form";
import { useAppDispatch, useAppSelector } from "../../..";
import {
  closeOfferPrice,
  deleteOfferPrice,
  editOfferPrice,
  fetchOfferPrice,
  fetchServiceRateVersions,
  offeringActions,
} from "../../../store/slices/offering/offering";
import {
  checkIsLoading,
  toHttpFormat,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
} from "../../../lib/utils";
import { OfferPriceParamsEdit } from "../../../components/OfferPrice/OfferPriceParamsEdit/OfferPriceParamsEdit";
import { IOfferPrice, IOfferPriceEditParams } from "../../../lib/types/OfferPrice";
import { IViewModeExtended } from "../../../lib/types/IViewMode";
import { ReactComponent as SubmitButtonIcon } from "../../../img/buttons_icons/submitButton_icon.svg";
import { ReactComponent as CancelButtonIcon } from "../../../img/buttons_icons/cancelButton_icon.svg";
import { ReactComponent as AddVersionIcon } from "../../../img/versions_icons/addVersion_icon.svg";
import { ReactComponent as CloseVersionIcon } from "../../../img/versions_icons/closeVersion_icon.svg";
import { ReactComponent as EditIcon } from "../../../img/versions_icons/editVersion_icon.svg";
import { ReactComponent as DeleteIcon } from "../../../img/delete.svg";
import { CharacteristicsContainer } from "../../../modules/policies";
import { useCharacteristicsInitialisation } from "../../../modules/policies/pages/CharacteristicsContainer/utils/useCharacteristicsInitialisation";
import { getCharFormatItem } from "../../../modules/policies/services/getCharFormatItem";
import { useOfferContext } from "../context/OfferContext";
import "./OfferPriceViewContainer.less";

export const OfferPriceViewContainer = () => {
  const history = useHistory<{ isProductDetails?: boolean }>();
  const dispatch = useAppDispatch();
  const { search } = useLocation();
  const { offerId, priceId } = useParams<{ offerId: string; priceId: string }>();
  const { priceCreationAllowed, reloadOffer, hasActiveOfferVersion } = useOfferContext();
  const offerOnDate = new URLSearchParams(search).get("onDate")?.replace(" ", "+");
  const onDatePrice = new URLSearchParams(search).get("onDatePrice")?.replace(" ", "+");

  const [offerPriceParamsForm] = useForm();

  const { tax } = useAppSelector((state) => state.taxTs);
  const { offerPrice, closeOfferPriceLoading } = useAppSelector((state) => state.offering);
  const { offer } = useAppSelector((state) => state.offering);
  const { aggregationRules } = useAppSelector((state) => state.aggregationRules);

  const { tenant } = useAppSelector((state) => state.tenants);
  const currencyId = tenant.defaultCurrencyId;

  const toOfferId = () => {
    if (history.location.state?.isProductDetails) {
      history.goBack();
    } else {
      history.push(`/offer/${offerId}?onDate=${offerOnDate}`);
    }
  };

  const loadOfferPrice = () => {
    dispatch(fetchOfferPrice({ rateId: priceId, onDate: toHttpFormat(onDatePrice) })).then(
      (res) => {
        if (!res.payload) {
          toOfferId();
        }
      }
    );

    dispatch(fetchServiceRateVersions(Number(priceId)));
  };

  useEffect(() => {
    priceId && loadOfferPrice();
    return () => {
      dispatch(offeringActions.cleanOfferPrice());
    };
  }, [priceId, search]);

  const [viewMode, setViewMode] = useState<IViewModeExtended>("view");

  const handleSetViewMode = (newViewMode: IViewModeExtended) => {
    setViewMode(newViewMode);
  };

  // ---> Характеристики <---
  const {
    fromDateCharacteristic,
    onChangeFromDateCharacteristic,
    onCheckFieldsError,
    fieldsError,
    infoCharacteristic,
    charValuesCharacteristic,
    setInfoCharacteristic,
    setCharValuesCharacteristic,
    onExpandAddVersionForCharacteristics,
  } = useCharacteristicsInitialisation({
    initialFromDate: offerPrice?.fromDate,
  });
  // ---> Характеристики <---

  const onFinish = (values) => {
    const info = infoCharacteristic.map((el) => getCharFormatItem(el, 1035));
    const charValues = charValuesCharacteristic.map((el) => getCharFormatItem(el));

    if (viewMode === "edit") {
      const onSaveEdit = () => {
        const isValid = onCheckFieldsError(["infoCharacteristic", "charValuesCharacteristic"]);

        if (!isValid) {
          return;
        }

        const params: IOfferPriceEditParams["params"] = {
          // Данные из формы
          aggregationRuleId: values.aggregationRuleId,
          price: values.price,
          fromDate: toHttpFormatStartOfDay(values?.fromDate),
          toDate: values.toDate ? toHttpFormatEndOfDay(values?.toDate) : undefined,
          name: values.name,
          rateModeId: values.rateModeId,
          priceEventId: values.priceEventId,
          ratePeriodId: values.ratePeriodId,
          rateTypeId: values.rateTypeId,
          currencyId: values.currencyId,
          chargeNameId: values.chargeNameId,
          priceUnitId: values.priceUnitId,
          conditionTypeId: values.conditionTypes?.reduce((acc, cur) => acc + cur) || 0,
          conditionTypes: values.conditionTypes,
          dsc: values?.dsc,
          rangeFrom: values?.rangeFrom,
          rangeTo: values?.rangeTo,
          // Дополнительные данные
          offeringId: Number(offerId),
          info,
          charValues,
        };
        dispatch(
          editOfferPrice({
            rateId: Number(priceId),
            fromDate: toHttpFormat(offerPrice?.fromDate),
            force: viewMode === "edit",
            params,
          })
        ).then(() => {
          handleSetViewMode("view");
          reloadOffer();
          loadOfferPrice();
          offerPriceParamsForm.resetFields();
        });
      };

      // Если раннее текущей даты, то запрашиваем подтверждение, что вносим изменения старой датой
      if (moment(values?.toDate).isBefore(moment(), "day")) {
        const onCancel = () => {
          handleSetViewMode("view");
          offerPriceParamsForm.resetFields();
        };
        Modal.confirm({
          onOk: () => {
            onSaveEdit();
          },
          title: (
            <>
              Вы вносите исправления старой датой.
              <br /> Вы подтверждаете изменение?
            </>
          ),
          okText: "Подтвердить",
          cancelText: "Отмена",
          onCancel,
        });
      } else {
        onSaveEdit();
      }
    }

    if (viewMode === "close") {
      // Если раннее текущей даты, то запрашиваем подтверждение, что вносим изменения старой датой
      if (moment(values?.toDate).isBefore(moment(), "day")) {
        const onCancel = () => {
          handleSetViewMode("view");
          offerPriceParamsForm.resetFields();
        };

        Modal.confirm({
          onOk: () => {
            dispatch(
              closeOfferPrice({
                rateId: Number(priceId),
                fromDate: toHttpFormatEndOfDay(values?.toDate),
              })
            ).then(() => {
              handleSetViewMode("view");
              reloadOffer();
              loadOfferPrice();
              offerPriceParamsForm.resetFields();
            });
          },
          title: (
            <>
              Вы вносите исправления старой датой.
              <br /> Вы подтверждаете изменение?
            </>
          ),
          okText: "Подтвердить",
          cancelText: "Отмена",
          onCancel,
        });
      } else {
        dispatch(
          closeOfferPrice({
            rateId: Number(priceId),
            fromDate: toHttpFormatEndOfDay(values?.toDate),
          })
        ).then(() => {
          handleSetViewMode("view");
          loadOfferPrice();
          offerPriceParamsForm.resetFields();
        });
      }
    }

    if (viewMode === "add") {
      const onSave = () => {
        const isValid = onCheckFieldsError(["infoCharacteristic", "charValuesCharacteristic"]);

        if (!isValid) {
          return;
        }

        const params: IOfferPriceEditParams["params"] = {
          // Данные из формы
          aggregationRuleId: values.aggregationRuleId,
          price: values.price,
          fromDate: toHttpFormatStartOfDay(values?.fromDate),
          toDate: values.toDate ? toHttpFormatEndOfDay(values?.toDate) : undefined,
          name: values.name,
          rateModeId: values.rateModeId,
          priceEventId: values.priceEventId,
          ratePeriodId: values.ratePeriodId,
          rateTypeId: values.rateTypeId,
          currencyId: values.currencyId,
          chargeNameId: values.chargeNameId,
          priceUnitId: values.priceUnitId,
          conditionTypeId: values.conditionTypes?.reduce((acc, cur) => acc + cur) || 0,
          conditionTypes: values.conditionTypes,
          dsc: values?.dsc,
          rangeFrom: values?.rangeFrom,
          rangeTo: values?.rangeTo,
          // Дополнительные данные
          offeringId: Number(offerId),
          info,
          charValues,
        };
        dispatch(
          editOfferPrice({
            rateId: Number(priceId),
            fromDate: toHttpFormat(values?.fromDate),
            force: false,
            params,
          })
        ).then((res) => {
          handleSetViewMode("view");
          if (res?.payload) {
            const { fromDate } = res.payload as IOfferPrice;
            const onDateAddedPrice = toHttpFormat(fromDate);
            history.replace(
              `/offer/${offerId}/price/${priceId}?onDate=${onDateAddedPrice}&onDatePrice=${onDateAddedPrice}`
            );
          }
          offerPriceParamsForm.resetFields();
        });
      };

      // Если раннее текущей даты, то запрашиваем подтверждение, что вносим изменения старой датой
      if (moment(values?.toDate).isBefore(moment(), "day")) {
        const onCancel = () => {
          handleSetViewMode("view");
          offerPriceParamsForm.resetFields();
        };
        Modal.confirm({
          onOk: () => {
            onSave();
          },
          title: (
            <>
              Вы вносите исправления старой датой.
              <br /> Вы подтверждаете изменение?
            </>
          ),
          okText: "Подтвердить",
          cancelText: "Отмена",
          onCancel,
        });
      } else {
        onSave();
      }
    }
  };

  useEffect(() => {
    if (offer && currencyId) {
      offerPriceParamsForm.setFieldsValue({
        fromDate:
          offer?.fromDate && moment().startOf("d").isAfter(moment(offer?.fromDate).startOf("d"))
            ? moment()
            : moment(offer?.fromDate),
        priceEventId: 1,
        currencyId,
      });
    }
  }, [offer, currencyId]);

  // const isLatestVersion = useMemo(() => {
  //   if (serviceRateVersions?.length > 0) {
  //     const sortedServiceRateVersions = [...serviceRateVersions]?.sort((a, b) =>
  //       moment(a?.fromDate).isBefore(moment(b?.fromDate)) ? 1 : -1
  //     );

  //     return moment(sortedServiceRateVersions?.[0].fromDate).isSame(
  //       moment(offerPrice?.fromDate)
  //     );
  //   }

  //   return false;
  // }, [offerPrice, serviceRateVersions]);

  // const isPastVersion = useMemo(() => {
  //   const fromDate = offerPrice?.fromDate && moment(offerPrice.fromDate);
  //   const toDate = offerPrice?.toDate && moment(offerPrice.toDate);
  //   if (fromDate && toDate) {
  //     return toDate.isBefore(moment());
  //   }
  //   return false;
  // }, [offerPrice]);

  const handleOnCancel = () => {
    handleSetViewMode("view");
  };

  const handleOnCloseDate = () => {
    handleSetViewMode("close");
    offerPriceParamsForm.setFieldsValue({
      fromDate: moment(offerPrice?.fromDate),
      toDate: moment(offerPrice?.toDate),
    });
  };

  const handleOnEdit = () => {
    handleSetViewMode("edit");

    const price = Number(offerPrice?.price) || 0;
    const taxRate = tax?.taxRate || "0";
    const taxMultiplier = Number(taxRate) / 100 + 1;

    const priceWithVat = round(price * taxMultiplier, 2).toFixed(2);

    offerPriceParamsForm.setFieldsValue({
      aggregationRuleId: offerPrice?.aggregationRuleId,
      price: offerPrice?.price,
      toDate: moment(offerPrice?.toDate),
      fromDate: moment(offerPrice?.fromDate),
      name: offerPrice?.name,
      rateModeId: offerPrice?.rateModeId,
      priceEventId: offerPrice?.priceEventId,
      // ratePeriodId: offerPrice?.ratePeriodId,
      rateTypeId: offerPrice?.rateTypeId,
      currencyId: offerPrice?.currencyId,
      chargeNameId: offerPrice?.chargeNameId,
      priceUnitId: offerPrice?.priceUnitId,
      // conditionTypeId: [offerPrice?.conditionTypeId],
      conditionTypes: offerPrice?.conditionTypes?.length ? offerPrice.conditionTypes : [0],
      // dsc: offerPrice?.dsc,
      rangeFrom: offerPrice?.rangeFrom,
      rangeTo: offerPrice?.rangeTo,
      priceWithVat,
    });
  };

  const handleOnAdd = () => {
    handleSetViewMode("add");

    const price = Number(offerPrice?.price) || 0;
    const taxRate = tax?.taxRate || "0";
    const taxMultiplier = Number(taxRate) / 100 + 1;

    const priceWithVat = round(price * taxMultiplier, 2).toFixed(2);

    onExpandAddVersionForCharacteristics(offerPrice?.fromDate);

    offerPriceParamsForm.setFieldsValue({
      aggregationRuleId: offerPrice?.aggregationRuleId,
      price: offerPrice?.price,
      fromDate: moment(offerPrice?.fromDate).add(1, "d"),
      toDate: moment(offerPrice?.toDate),
      name: offerPrice?.name,
      rateModeId: offerPrice?.rateModeId,
      priceEventId: offerPrice?.priceEventId,
      // ratePeriodId: offerPrice?.ratePeriodId,
      rateTypeId: offerPrice?.rateTypeId,
      currencyId: offerPrice?.currencyId,
      chargeNameId: offerPrice?.chargeNameId,
      priceUnitId: offerPrice?.priceUnitId,
      // conditionTypeId: [offerPrice?.conditionTypeId],
      conditionTypes: offerPrice?.conditionTypes?.length ? offerPrice.conditionTypes : [0],
      // dsc: offerPrice?.dsc,
      rangeFrom: offerPrice?.rangeFrom,
      rangeTo: offerPrice?.rangeTo,
      priceWithVat,
    });
  };

  const handleOnDeleteConfirmModal = () => {
    Modal.confirm({
      onOk: () => {
        dispatch(deleteOfferPrice(Number(priceId))).then(() => {
          toOfferId();
          reloadOffer();
        });
      },
      title: (
        <>
          Цена будет удалена.
          <br /> Вы подтверждаете удаление?
        </>
      ),
      okText: "Подтвердить",
      cancelText: "Отмена",
    });
  };

  const renderButtons = () => {
    const isProductDetails = !!search
      ?.replace("?", "")
      ?.split("&")
      ?.find((el) => el.includes("isProductDetails"))
      ?.replace("isProductDetails=", "");

    if (isProductDetails) {
      return null;
    }

    if (viewMode === "edit" || viewMode === "close" || viewMode === "add") {
      return (
        <Row
          align="middle"
          className={`offer-price-view-container__${
            viewMode === "close" ? "edit__close__btns" : "edit__btns"
          }`}
          justify="end"
        >
          <Col>
            <Tooltip title="Подтвердить">
              <SubmitButtonIcon
                onClick={offerPriceParamsForm.submit}
                className={`offer-price-view-container__iconBtn ${
                  checkIsLoading(closeOfferPriceLoading, closeOfferPriceLoading)
                    ? "pointer-event-none"
                    : ""
                }`}
              />
            </Tooltip>
          </Col>
          <Col>
            <Tooltip title="Отменить">
              <CancelButtonIcon
                onClick={handleOnCancel}
                className="offer-price-view-container__iconBtn"
              />
            </Tooltip>
          </Col>
        </Row>
      );
    }

    if (viewMode === "view") {
      return (
        <Row gutter={7} align="middle" justify="end">
          {priceCreationAllowed && (
            <>
              <Col>
                <Tooltip title="Добавить версию">
                  <AddVersionIcon
                    onClick={handleOnAdd}
                    className="offer-price-view-container__iconBtn"
                  />
                </Tooltip>
              </Col>
              <Col>
                <Tooltip title="Редактировать">
                  <EditIcon
                    onClick={handleOnEdit}
                    className="offer-price-view-container__iconBtn"
                  />
                </Tooltip>
              </Col>
              <Col>
                <Tooltip title="Изменить дату закрытия">
                  <CloseVersionIcon
                    onClick={handleOnCloseDate}
                    className="offer-price-view-container__iconBtn"
                  />
                </Tooltip>
              </Col>
            </>
          )}
          {!hasActiveOfferVersion && (
            <Col>
              <Tooltip title="Удалить">
                <DeleteIcon
                  onClick={handleOnDeleteConfirmModal}
                  className="offer-price-view-container__iconBtn"
                />
              </Tooltip>
            </Col>
          )}
        </Row>
      );
    }
    return null;
  };

  return (
    <div className="offer-price-view-container">
      <h1>
        <Tooltip
          title={
            history.location.state?.isProductDetails ? "Вернуться назад" : "К просмотру предложения"
          }
        >
          <LeftOutlined onClick={toOfferId} className="offer-price-view-container__backBtn" />
        </Tooltip>
        Просмотр цены
      </h1>
      <Divider className="offer-price-view-container__upperDivider" />
      <h3 style={{ margin: "8px 0 0 4px" }}>
        {`Цена для предложения ${offer?.extName || ""} ${
          offer?.name ? `(${String(offer?.name)})` : ""
        }`}
      </h3>

      {renderButtons()}

      <Form form={offerPriceParamsForm} labelAlign="left" onFinish={onFinish}>
        <OfferPriceParamsEdit
          viewMode={viewMode}
          offerPriceParamsForm={offerPriceParamsForm}
          offer={offer}
          onChangeFromDateCharacteristic={onChangeFromDateCharacteristic}
          aggregationRules={aggregationRules}
        />
        {viewMode === "add" && (
          <OfferPriceParamsEdit
            isChild
            viewMode="view"
            offerPriceParamsForm={offerPriceParamsForm}
            offer={offer}
            aggregationRules={aggregationRules}
          />
        )}
      </Form>

      <CharacteristicsContainer
        objectTypeId={1035}
        mode={viewMode}
        title="Характеристики цены"
        tableData={infoCharacteristic}
        setTableData={setInfoCharacteristic}
        dataSaved={offerPrice?.info}
        fromDateCharacteristic={fromDateCharacteristic}
        messageWarningNotFromDate="Необходимо выбрать Дату создания цены"
        fieldsError={fieldsError}
      />
      <CharacteristicsContainer
        objectTypeId={1032}
        mode={viewMode}
        title="Параметры доступности цены"
        isInheritableObject
        newObjectChars={offer?.productCharValues}
        isNewObjectChars
        tableData={charValuesCharacteristic}
        setTableData={setCharValuesCharacteristic}
        dataSaved={offerPrice?.charValues}
        fromDateCharacteristic={fromDateCharacteristic}
        messageWarningNotFromDate="Необходимо выбрать Дату создания цены"
        disabledValidation
        isDefaultDisabled
        fieldsError={fieldsError}
      />
      <Collapse bordered={false} defaultActiveKey="1">
        <Collapse.Panel
          key="1"
          header={
            <Row justify="space-between" className="offer-price-view-container__pageTitle">
              <h3>Выражение</h3>
            </Row>
          }
        />
      </Collapse>
      <Divider style={{ margin: 0 }} />
      <Collapse bordered={false} defaultActiveKey="1">
        <Collapse.Panel
          key="1"
          header={
            <Row justify="space-between" className="offer-price-view-container__pageTitle">
              <h3>Тарифное правило</h3>
            </Row>
          }
        />
      </Collapse>
      <Divider style={{ margin: 0 }} />
    </div>
  );
};
